import React, { Dispatch, SetStateAction, useState } from 'react'
import CustomModalTabList from '../../../../../components/ui/CustomModalTabList/CustomModalTabList';
import { useAppDispatch } from '../../../../../store/store';
import { getMercuryoCurrenciesThunk, createMercuryoWidgetLinkThunk } from '../../../../../store/reducers/ActionCreators';
import { Loader } from '../../../../../components/Loader/Loader';
import MyBtn from '../../../../../components/ui/MyBtn/MyBtn';
import MyInput from '../../../../../components/ui/MyInput/MyInput';
import CurrencyDropdown from '../../../../../components/ui/CurrencyDropdown/CurrencyDropdown';

type PropsType = {
    s: any;
    token: any;
    isLoading: any;
    selectedWallet: any;
    isModalOpen: any;
    setIsModalOpen: Dispatch<SetStateAction<any>>;
    setSuccessMessage: Dispatch<SetStateAction<any>>;
    setIsSuccessModalOpen: Dispatch<SetStateAction<any>>;
    setErrorMessage: Dispatch<SetStateAction<any>>;
    setIsErrorModalOpen: Dispatch<SetStateAction<any>>;
}

const TransactionByCardModal = (props: PropsType) => {

    const {
        s,
        token,
        isLoading,
        selectedWallet,
        isModalOpen,
        setIsModalOpen,
        setSuccessMessage,
        setIsSuccessModalOpen,
        setErrorMessage,
        setIsErrorModalOpen
    } = props;

    const dispatch = useAppDispatch();
    const [modalDismissAllowed, setModalDismissAllowed] = useState(true);
    
    const [fiatCurrency, setFiatCurrency] = useState('USD');
    const [fiatAmount, setFiatAmount] = useState('');
    const [paymentLink, setPaymentLink] = useState(null);
    const [widgetLoading, setWidgetLoading] = useState(false);
    const [currencies, setCurrencies] = useState<any[]>([]);
    const [currenciesLoading, setCurrenciesLoading] = useState(true);

    const resetToInitialState = () => {
        setFiatAmount('');
        setPaymentLink(null);
        setWidgetLoading(false);
        setModalDismissAllowed(true);
        const usdCurrency = currencies.find((c: any) => c.code === 'USD');
        if (usdCurrency) {
            setFiatCurrency('USD');
        }
    };

    const handleTabChange = (tabName: string) => {
        resetToInitialState();
    };

    const handleWidgetLink = async (type: 'buy' | 'sell') => {
        if (!fiatAmount || parseFloat(fiatAmount) <= 0) {
            setErrorMessage("Please enter a valid amount");
            setIsErrorModalOpen(true);
            return;
        }

        try {
            setWidgetLoading(true);
            
            const requestData = {
                walletId: selectedWallet?.id,
                fiatCurrency: fiatCurrency,
                fiatAmount: parseFloat(fiatAmount),
                type: type
            };

            const response = await dispatch(createMercuryoWidgetLinkThunk(token, requestData));

            if (response.data?.paymentLink) {
                setPaymentLink(response.data.paymentLink);
                setModalDismissAllowed(false);
            } else {
                setErrorMessage("Failed to generate payment link");
                setIsErrorModalOpen(true);
            }
        } catch (error: any) {
            console.error("Widget link error:", error);
            const errorMessage = error?.response?.data?.error || error?.message || "An error occurred, please try again!";
            setErrorMessage(errorMessage);
            setIsErrorModalOpen(true);
        } finally {
            setWidgetLoading(false);
        }
    };

    const handleIframeMessage = (event: MessageEvent) => {
        if (event.data?.type === 'PAYMENT_SUCCESS') {
            setModalDismissAllowed(true);
            setPaymentLink(null);
            setIsModalOpen(false);
            setSuccessMessage("Transaction has been successfully made");
            setIsSuccessModalOpen(true);
        } else if (event.data?.type === 'PAYMENT_ERROR') {
            setModalDismissAllowed(true);
            setPaymentLink(null);
            setErrorMessage("Payment failed, please try again!");
            setIsErrorModalOpen(true);
        }
    };

    const fetchCurrencies = async () => {
        try {
            const response = await dispatch(getMercuryoCurrenciesThunk(token));
            if (response.data?.currencies) {
                setCurrencies(response.data.currencies);
                const usdCurrency = response.data.currencies.find((c: any) => c.code === 'USD');
                if (usdCurrency) {
                    setFiatCurrency('USD');
                }
            }
        } catch (error: any) {
            console.error('Failed to fetch currencies:', error);
            
            if (error?.response?.status === 401 || error?.response?.status === 404) {
                setErrorMessage("Authentication failed. Please refresh and try again.");
                setIsErrorModalOpen(true);
                setIsModalOpen(false);
                return;
            }
            
            setCurrencies([
                { code: 'USD', name: 'US Dollar', icon: null },
                { code: 'EUR', name: 'Euro', icon: null },
                { code: 'GBP', name: 'British Pound', icon: null }
            ]);
        } finally {
            setCurrenciesLoading(false);
        }
    };

    React.useEffect(() => {
        window.addEventListener('message', handleIframeMessage);
        return () => window.removeEventListener('message', handleIframeMessage);
    }, []);

    React.useEffect(() => {
        if (isModalOpen) {
            fetchCurrencies();
        }
    }, [isModalOpen]);

    const renderPaymentForm = (type: 'buy' | 'sell') => (
        paymentLink ? (
            <div style={{
                margin: window.innerWidth <= 768 ? '-40px -20px -40px -20px' : '-60px -125px -60px -125px',
                height: 'calc(85vh - 80px)',
                minHeight: '600px',
                backgroundColor: '#fff',
                borderRadius: '0 0 12px 12px'
            }}>
                <iframe
                    src={paymentLink}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: 'block',
                        borderRadius: '0 0 12px 12px'
                    }}
                    title="Payment Gateway"
                />
            </div>
        ) : (
            <div className={s.formWrapper} style={{ padding: '20px' }}>
                {currenciesLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Loader />
                        <p style={{ marginTop: '10px', color: '#666' }}>Loading currencies...</p>
                    </div>
                ) : widgetLoading ? (
                    <Loader />
                ) : (
                <div>
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#2A2E37',
                                fontFamily: 'Inter, sans-serif'
                            }}>
                                {type === 'buy' ? 'Amount to spend' : 'Amount to receive'}
                            </label>
                            <MyInput
                                value={fiatAmount}
                                onChange={(e) => setFiatAmount(e.target.value)}
                                placeholder="Enter amount"
                            />
                        </div>
                        
                        <div>
                            <label style={{
                                display: 'block', 
                                marginBottom: '8px', 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#2A2E37',
                                fontFamily: 'Inter, sans-serif'
                            }}>
                                Currency
                            </label>
                            <CurrencyDropdown
                                id={`currency-dropdown-${type}`}
                                data={currencies}
                                value={fiatCurrency}
                                placeholder="Select currency"
                                onChange={(currency) => setFiatCurrency(currency.code)}
                            />
                        </div>
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ 
                            color: '#6B7280', 
                            fontSize: '14px', 
                            marginBottom: '12px',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: '400'
                        }}>
                            {type === 'buy' 
                                ? `You will receive ${selectedWallet?.currency} in your wallet`
                                : `You will send ${selectedWallet?.currency} from your wallet`
                            }
                        </p>
                        <div style={{ 
                            background: '#F9FAFB', 
                            padding: '12px', 
                            borderRadius: '8px',
                            border: '1.5px solid #E5E7EB',
                            fontFamily: 'Inter, sans-serif'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: '600', color: '#2A2E37', fontSize: '14px' }}>Wallet:</span>
                                <span style={{ 
                                    color: '#6B7280', 
                                    fontSize: '12px',
                                    fontFamily: 'Monaco, Consolas, monospace',
                                    wordBreak: 'break-all',
                                    maxWidth: '200px',
                                    textAlign: 'right'
                                }}>
                                    {selectedWallet?.address}
                                </span>
                            </div>
                        </div>
                    </div>

                    <MyBtn
                        title={type === 'buy' ? "Continue to Buy" : "Continue to Sell"}
                        large
                        onClick={() => handleWidgetLink(type)}
                        type="button"
                    />
                </div>
                )}
            </div>
        )
    );

    return (
        <CustomModalTabList
            isModalOpen={isModalOpen}
            onTabChange={handleTabChange}
            tabs={[
                {
                    label: "Buy",
                    action: async () => {},
                },
                {
                    label: "Sell",
                    action: async () => {},
                },
            ]}
            tabContents={{
                Buy: renderPaymentForm('buy'),
                Sell: renderPaymentForm('sell'),
            }}
            onClose={() => {
                if (modalDismissAllowed) {
                    setIsModalOpen(false);
                    resetToInitialState();
                    setCurrenciesLoading(true);
                }
            }}
        />
    )
}

export default TransactionByCardModal;